import React from 'react';
import {
  Users,
  Lightbulb,
  Zap,
  CheckCircle,
  ArrowRight,
  Target,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEOHead } from '../components/SEOHead';
import { useNavigate } from 'react-router-dom';

export default function AboutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      <SEOHead
        title="À Propos | Sun Is Up - Qui sommes-nous ?"
        description="Sun Is Up, communauté d'énergie locale à Bruxelles. Deux ingénieurs, une conviction : l'énergie locale doit profiter à ceux qui la produisent et la consomment."
        keywords="communauté d'énergie bruxelles, qui sommes-nous sun is up, histoire sun is up, mission transition énergétique belgique, partage d'énergie bruxelles"
        url="https://sunisup.be/about"
        logo="https://sunisup.be/images/logo.png"
      />
      <div className="min-h-screen bg-white font-sans">
        {/* Hero Section */}
        <section className="py-20 lg:py-32" style={{ backgroundColor: '#FFF5E1' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-6 font-sans" style={{ color: '#1a1a1a' }}>
                {t('aboutPage.hero.title')}
              </h1>
              <p className="text-lg sm:text-xl lg:text-2xl max-w-3xl mx-auto font-sans" style={{ color: '#4a4a4a' }}>
                {t('aboutPage.hero.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Notre histoire */}
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                <Lightbulb className="w-6 h-6" style={{ color: '#F8BD3D' }} />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold font-sans" style={{ color: '#1a1a1a' }}>
                {t('aboutPage.history.title')}
              </h2>
            </div>
            <div className="space-y-4 text-lg leading-relaxed font-sans" style={{ color: '#4a4a4a' }}>
              <p>{t('aboutPage.history.p1')}</p>
              <p>{t('aboutPage.history.p2')}</p>
            </div>
          </div>
        </section>

        {/* Notre mission */}
        <section className="py-16 sm:py-24" style={{ backgroundColor: '#fafafa' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                <Target className="w-6 h-6" style={{ color: '#F8BD3D' }} />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold font-sans" style={{ color: '#1a1a1a' }}>
                {t('aboutPage.mission.title')}
              </h2>
            </div>
            <p className="text-lg leading-relaxed font-sans" style={{ color: '#4a4a4a' }}>
              {t('aboutPage.mission.text')}
            </p>
          </div>
        </section>

        {/* Bios Thomas et Simon */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 items-center">
              {/* Thomas - Left side */}
              <div className="bg-white rounded-lg shadow-md p-8 border hover:shadow-lg transition-all duration-300" style={{ borderColor: '#15A759' }}>
                <div className="text-center mb-6">
                  <div className="w-20 h-20 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#E8F5EE' }}>
                    <Users className="w-10 h-10" style={{ color: '#15A759' }} />
                  </div>
                  <h3 className="text-2xl font-semibold text-neutral-800 mb-2 font-sans">{t('about.whoAreWe.thomas.title')}</h3>
                  <p className="font-medium text-base font-sans" style={{ color: '#15A759' }}>{t('about.whoAreWe.thomas.role')}</p>
                </div>

                <div className="space-y-6">
                  {t('about.whoAreWe.thomas.qualifications', { returnObjects: true }).map((qualification: string, index: number) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#15A759' }}></div>
                      <div>
                        {qualification.includes('\n') ? (
                          <div>
                            <p className="font-bold text-neutral-900 text-lg font-sans">{qualification.split('\n')[0]}</p>
                            <p className="text-neutral-600 mt-2 font-sans">{qualification.split('\n')[1]}</p>
                          </div>
                        ) : (
                          <p className="text-neutral-700 font-sans"><strong className="text-neutral-900">{qualification.split(' - ')[0]}</strong> - {qualification.split(' - ')[1]}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Photo - Center */}
              <div className="relative lg:order-first lg:col-span-1">
                <div className="aspect-[4/5] rounded-lg shadow-lg overflow-hidden relative">
                  <img
                    src="/images/Sim-Tomv2.jpg"
                    alt="Thomas et Simon, fondateurs de Sun Is Up"
                    className="w-full h-full object-cover"
                    loading="eager"
                    fetchPriority="high"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
                <div className="absolute -bottom-6 -right-6 bg-white rounded-lg shadow-lg p-4 border border-brand-gold">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-brand-gold/10 rounded-lg flex items-center justify-center">
                      <Lightbulb className="w-6 h-6 text-brand-gold" />
                    </div>
                    <div>
                      <p className="font-semibold text-neutral-800 text-base">{t('aboutPage.photoBadge.date')}</p>
                      <p className="text-neutral-600 text-sm">{t('aboutPage.photoBadge.label')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Simon - Right side */}
              <div className="bg-white rounded-lg shadow-md p-8 border border-brand-gold/20 hover:border-brand-gold/30 hover:shadow-lg transition-all duration-300">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-brand-gold/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Zap className="w-10 h-10 text-brand-gold" />
                  </div>
                  <h3 className="text-2xl font-semibold text-neutral-800 mb-2 font-sans">{t('about.whoAreWe.simon.title')}</h3>
                  <p className="text-brand-gold font-medium text-base font-sans">{t('about.whoAreWe.simon.role')}</p>
                </div>

                <div className="space-y-6">
                  {t('about.whoAreWe.simon.qualifications', { returnObjects: true }).map((qualification: string, index: number) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-brand-gold rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        {qualification.includes('\n') ? (
                          <div>
                            <p className="font-bold text-neutral-900 text-lg font-sans">{qualification.split('\n')[0]}</p>
                            <p className="text-neutral-600 mt-2 font-sans">{qualification.split('\n')[1]}</p>
                          </div>
                        ) : (
                          <p className="text-neutral-700 font-sans"><strong className="text-neutral-900">{qualification.split(' - ')[0]}</strong> - {qualification.split(' - ')[1]}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA vers Nos réalisations */}
        <section className="py-16 sm:py-24" style={{ backgroundColor: '#FFF5E1' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 font-sans" style={{ color: '#1a1a1a' }}>
              {t('aboutPage.cta.title')}
            </h2>
            <button
              onClick={() => navigate('/nos-realisations')}
              className="px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 inline-flex items-center gap-3 font-sans"
              style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
            >
              {t('aboutPage.cta.button')}
              <ArrowRight className="w-6 h-6" />
            </button>
          </div>
        </section>
      </div>
    </>
  );
}
